"use client";
import { sanitizeCmsHtml } from "utils/safeHtml";
import React, { useState } from "react";
import Link from "components/common/Link";
import { Modal } from "antd";
import {
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  Repeat,
  UserRound,
  Wallet,
} from "lucide-react";
import Image from "components/common/Image";
import { formatPrice } from "utils/currencyFormatter";
import { formatDate } from "utils/dateFormatter";
import dayjs from "utils/day";
import { useT } from "providers/I18nProvider";
import type { PackageInfo } from "services/prepaidPackage/formatter";

interface PackageDetailModalProps {
  open: boolean;
  pkg: PackageInfo | null;
  onClose: () => void;
  onBuy: () => void;
}

/** Modal chi tiết gói trả trước: tóm tắt quyền lợi, chi nhánh áp dụng, điều khoản, CTA về app */
const PackageDetailModal: React.FC<PackageDetailModalProps> = ({
  open,
  pkg,
  onClose,
  onBuy,
}) => {
  const t = useT();
  const [termsOpen, setTermsOpen] = useState(false);
  if (!pkg) return null;

  const fmtDate = (d: any) =>
    d && dayjs(d).isValid() ? formatDate(d, "DD/MM/YYYY") : "";
  const endD = fmtDate(pkg.endDate);

  const facts = [
    {
      icon: <Repeat size={18} />,
      label: t("packageDetail.sessions"),
      value: t("home.packageSessions", { count: pkg.quantity }),
    },
    {
      icon: <Wallet size={18} />,
      label: t("packageDetail.pricePerSession"),
      value: formatPrice(pkg.pricePerUse),
      sub:
        pkg.menuPrice > pkg.pricePerUse
          ? `${t("home.packageRetail")} ${formatPrice(pkg.menuPrice)}`
          : "",
    },
    {
      icon: <Clock size={18} />,
      label: t("packageDetail.validity"),
      value: pkg.durationMonths
        ? t("home.packageMonths", { count: pkg.durationMonths })
        : t("packageDetail.days", { count: pkg.durationDays }),
      sub: pkg.gracePeriodDays
        ? t("packageDetail.grace", { count: pkg.gracePeriodDays })
        : "",
    },
    {
      icon: <UserRound size={18} />,
      label: t("packageDetail.maxPerUser"),
      value: pkg.maxPerUser
        ? t("packageDetail.maxPerUserValue", { count: pkg.maxPerUser })
        : t("packageDetail.unlimited"),
    },
  ];

  return (
    <Modal
      rootClassName="lm-sheet"
      open={open}
      onCancel={onClose}
      afterClose={() => setTermsOpen(false)}
      footer={null}
      centered
      width={640}
      style={{ maxWidth: "92vw" }}
      styles={{
        mask: { backgroundColor: "rgba(0, 0, 0, 0.72)", backdropFilter: "blur(4px)" },
        content: { borderRadius: 20, padding: "clamp(16px, 4vw, 28px)" },
        header: { marginBottom: 16 },
      }}
      title={
        <span className="text-xl md:text-2xl font-semibold">{pkg.name}</span>
      }
    >
      <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* ===== Khối tóm tắt: thumbnail nhỏ + giá ===== */}
        <div className="flex gap-4 items-start">
          {pkg.cover && (
            <div className="relative flex-shrink-0 w-[128px] md:w-[168px]">
              <Image
                src={pkg.cover}
                alt={pkg.coverAlt}
                wrapClassName="relative w-full aspect-[4/3] rounded-xl object-cover overflow-hidden bg-gray-100"
              />
              {pkg.savingPct > 0 && (
                <span className="absolute left-2 top-2 rounded-md bg-red-500 px-2 py-0.5 text-xs font-bold text-white shadow">
                  {t("home.packageSave")} {pkg.savingPct}%
                </span>
              )}
            </div>
          )}

        {/* Giá gói */}
        <div className="flex-1 bg-gray-50 rounded-lg p-3 self-stretch">
          <p className="text-xs text-gray-500 !mb-1">
            {t("packageDetail.packagePrice")}
          </p>
          <div className="flex items-baseline gap-3 flex-wrap">
            <p className="text-primary font-bold text-2xl !mb-0">
              {formatPrice(pkg.price)}
            </p>
            {pkg.originalTotal > pkg.price ? (
              <p className="text-gray-400 line-through text-sm !mb-0">
                {formatPrice(pkg.originalTotal)}
              </p>
            ) : null}
            {pkg.savingAmount > 0 && (
              <span className="ml-auto self-center px-2 py-1 rounded-md bg-red-50 text-red-500 text-xs font-semibold">
                {t("packageDetail.saveAmount")} {formatPrice(pkg.savingAmount)}
              </span>
            )}
          </div>
          {pkg.originalTotal > pkg.price ? (
            <p className="text-xs text-gray-500 !mb-0 mt-0.5">
              {t("packageDetail.retailTotal", { count: pkg.quantity })}
            </p>
          ) : null}
        </div>
        </div>

        {/* Tóm tắt quyền lợi */}
        <div className="grid grid-cols-2 gap-2">
          {facts.map((f, i) => (
            <div
              key={i}
              className="flex items-start gap-2.5 rounded-lg border border-gray-100 p-3"
            >
              <span className="text-primary mt-0.5 shrink-0">{f.icon}</span>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 !mb-0">{f.label}</p>
                <p className="text-sm font-semibold text-black01 !mb-0">
                  {f.value}
                </p>
                {f.sub ? (
                  <p className="text-xs text-gray-400 !mb-0">{f.sub}</p>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {/* Dịch vụ áp dụng */}
        {pkg.serviceName && (
          <p className="text-[15px] text-gray-600 !mb-0">
            <span className="font-medium">{t("packageDetail.service")}: </span>
            {pkg.serviceName}
          </p>
        )}

        {/* Mô tả (nếu khác tên) */}
        {pkg.description && pkg.description !== pkg.name && (
          <p className="text-[15px] leading-relaxed text-gray-600 whitespace-pre-line !mb-0">
            {pkg.description}
          </p>
        )}

        {/* Chi nhánh áp dụng */}
        {pkg.shops.length > 0 && (
          <div>
            <p className="font-semibold text-base mb-2 flex items-center gap-1.5">
              <MapPin size={16} className="text-primary" />
              {t("packageDetail.applyAt")}
              {pkg.shops.length > 1 ? (
                <span className="text-xs font-normal text-gray-500">
                  ({t("home.packageBranches", { count: pkg.shops.length })})
                </span>
              ) : null}
            </p>
            <div className="flex flex-col gap-2">
              {pkg.shops.map((s) => {
                const inner = (
                  <>
                    {s.avatar ? (
                      <Image
                        src={s.avatar}
                        alt={s.name}
                        wrapClassName="w-11 h-11 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-black01 truncate !mb-0">
                        {s.name}
                      </p>
                      {s.displayedAddress ? (
                        <p className="text-xs text-gray-500 line-clamp-1 !mb-0">
                          {s.displayedAddress}
                        </p>
                      ) : null}
                    </div>
                  </>
                );
                const cls =
                  "flex items-center gap-3 rounded-lg border border-gray-100 p-2 hover:border-primary/40 transition-colors";
                return s.slug ? (
                  <Link key={s.id} href={`/${s.slug}`} className={cls}>
                    {inner}
                  </Link>
                ) : (
                  <div key={s.id} className={cls}>
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Điều khoản (collapsible) */}
        {pkg.termsHtml && (
          <div className="rounded-lg border border-gray-100">
            <button
              type="button"
              onClick={() => setTermsOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-left"
            >
              <span className="font-semibold text-base">
                {t("packageDetail.terms")}
              </span>
              {termsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {termsOpen && (
              <div
                className="package-terms px-3 pb-3 text-[14px] leading-relaxed text-gray-600"
                // HTML richtext từ CMS — lọc script/on*/javascript: trước khi render
                dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(pkg.termsHtml) }}
              />
            )}
          </div>
        )}

        {/* Hạn mua */}
        {endD && (
          <p className="text-xs text-gray-500 flex items-center gap-1.5 !mb-0">
            <CalendarDays size={14} />
            {t("packageDetail.buyBefore")}: {endD}
          </p>
        )}
      </div>

      {/* CTA */}
      <div className="pt-4">
        <button
          type="button"
          onClick={onBuy}
          className="w-full px-4 py-3 rounded-full bg-primary text-white text-base font-semibold hover:opacity-90 transition-opacity"
        >
          {t("packageDetail.buyNow")}
        </button>
        <p className="text-center text-xs text-gray-400 mt-2 !mb-0">
          {t("packageDetail.buyOnApp")}
        </p>
      </div>
    </Modal>
  );
};

export default PackageDetailModal;
